import type { Map as MlMap } from "maplibre-gl";
import { KIND_COLOUR } from "./palette";

interface Pulse { lng: number; lat: number; kind: "birth" | "death"; born: number; then: () => void; /** Overrides the birth/death colour (sector colouring). */ colour?: string }

const DURATION = { birth: 2600, death: 3200 };

/**
 * A canvas over the map for the arrival animation. Births ripple outwards; deaths collapse inwards and fade.
 * It is far cheaper than re-styling a GeoJSON layer every frame, and it idles when nothing is animating.
 */
export class PulseLayer {
  private readonly canvas = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D;
  private pulses: Pulse[] = [];
  private running = false;

  constructor(private readonly map: MlMap) {
    this.canvas.className = "pulse-layer";
    map.getContainer().appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d")!;
    const resize = () => {
      const r = devicePixelRatio || 1, el = map.getContainer();
      this.canvas.width = el.clientWidth * r;
      this.canvas.height = el.clientHeight * r;
      this.ctx.setTransform(r, 0, 0, r, 0, 0);
    };
    resize();
    map.on("resize", resize);
  }

  /** Animate one arrival, then call `then` (which settles the dot into the map layer). */
  add(p: Omit<Pulse, "born">): void {
    this.pulses.push({ ...p, born: performance.now() });
    if (!this.running) { this.running = true; requestAnimationFrame(this.frame); }
  }

  private frame = (now: number): void => {
    const { ctx, map } = this;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const zoomScale = Math.min(1.6, Math.max(0.7, map.getZoom() / 7));
    this.pulses = this.pulses.filter((p) => {
      const k = (now - p.born) / DURATION[p.kind];
      if (k >= 1) { p.then(); return false; }
      const { x, y } = map.project([p.lng, p.lat]);
      const colour = p.colour ?? KIND_COLOUR[p.kind];
      const ease = 1 - Math.pow(1 - k, 3);
      ctx.save();
      if (p.kind === "birth") {
        ctx.globalAlpha = (1 - k) * 0.9;
        ctx.strokeStyle = colour;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(x, y, (4 + 30 * ease) * zoomScale, 0, Math.PI * 2); ctx.stroke();
        ctx.globalAlpha = (1 - k) * 0.35;
        ctx.beginPath(); ctx.arc(x, y, (4 + 16 * ease) * zoomScale, 0, Math.PI * 2); ctx.stroke();
        ctx.globalAlpha = 1 - k * 0.5;
        ctx.shadowColor = colour; ctx.shadowBlur = 14;
        ctx.fillStyle = colour;
        ctx.beginPath(); ctx.arc(x, y, 3.2 * zoomScale, 0, Math.PI * 2); ctx.fill();
      } else {
        ctx.globalAlpha = (1 - k) * 0.8;
        ctx.strokeStyle = colour;
        ctx.lineWidth = 1.2;
        ctx.beginPath(); ctx.arc(x, y, (24 - 20 * ease) * zoomScale, 0, Math.PI * 2); ctx.stroke();
        ctx.globalAlpha = 1 - k * 0.8;
        ctx.shadowColor = colour; ctx.shadowBlur = 10;
        ctx.fillStyle = colour;
        ctx.beginPath(); ctx.arc(x, y, (4.5 - 2.5 * ease) * zoomScale, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      return true;
    });
    if (this.pulses.length) requestAnimationFrame(this.frame);
    else { this.running = false; ctx.clearRect(0, 0, this.canvas.width, this.canvas.height); }
  };
}
